import { Alert, Box, Divider, Stack, Typography } from "@mui/material";
import { formatEuro } from "@shared";

import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { useTranslation } from "@/translations";

import { TRANSLATION_BASE } from "../SeizableIncomeCalculatorDialog.utils";
import { SeizableIncomeCalculationResult } from "../hooks/useSeizableIncomeCalculator";

interface SeizableIncomeResultProps {
  result?: SeizableIncomeCalculationResult;
}

export function SeizableIncomeResult({ result }: SeizableIncomeResultProps) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();

  const isMaintenanceResult = result?.isMaintenanceSeizure ?? false;
  const parametersAvailable = result?.parametersAvailable ?? true;
  const euro = (value: number | null | undefined) =>
    value != null && !Number.isNaN(value) ? formatEuro(value, language) : "–";

  const income = result?.income;
  const baseAmountLabel = result?.hasSpecialPayments
    ? `${TRANSLATION_BASE}.result.baseAmountWithSpecialPayments`
    : `${TRANSLATION_BASE}.result.baseAmountWithoutSpecialPayments`;

  return (
    <Stack
      flex={1}
      gap={2}
      aria-live="polite"
      sx={{
        p: 2,
        bgcolor: "grey.50",
        borderRadius: 1,
      }}
    >
      <Typography variant="overline" color="text.secondary">
        {t(`${TRANSLATION_BASE}.calculationTitle`)}
      </Typography>

      {!result ? (
        <Typography color="text.secondary">{t(`${TRANSLATION_BASE}.result.empty`)}</Typography>
      ) : !parametersAvailable ? (
        <Alert severity="warning">{t(`${TRANSLATION_BASE}.result.unavailable`)}</Alert>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "auto 1fr auto",
            columnGap: 1.5,
            rowGap: 2,
            alignItems: "baseline",
          }}
        >
          <span />
          <Typography color="text.secondary">{t(`${TRANSLATION_BASE}.result.income`)}</Typography>
          <Typography align="right">{euro(income)}</Typography>

          {isMaintenanceResult ? (
            <>
              <Typography color="text.secondary">−</Typography>
              <Typography color="text.secondary">{t(`${TRANSLATION_BASE}.result.seizable291a`)}</Typography>
              <Typography color="error.main" align="right">
                {euro(result.standardSeizableAmount)}
              </Typography>

              <Typography color="text.secondary">=</Typography>
              <span />
              <Typography align="right" sx={{ textDecoration: "underline" }}>
                {euro(result.subtotalAfterStandard)}
              </Typography>

              <Typography color="text.secondary">−</Typography>
              <Typography color="text.secondary">{t(`${TRANSLATION_BASE}.result.seizable291b`)}</Typography>
              <Typography color="error.main" align="right">
                {euro(result.maintenanceAdditionalAmount)}
              </Typography>
            </>
          ) : (
            <>
              <Typography color="text.secondary">−</Typography>
              <Typography color="text.secondary">{t(`${TRANSLATION_BASE}.result.seizable`)}</Typography>
              <Typography color="error.main" align="right">
                {euro(result.seizableAmount)}
              </Typography>
            </>
          )}

          <Divider sx={{ gridColumn: "1 / -1" }} />

          <Typography color="text.secondary" fontWeight="bold">
            =
          </Typography>
          <Typography fontWeight="bold">{t(`${TRANSLATION_BASE}.result.retained`)}</Typography>
          <Typography variant="h2" align="right" sx={{ textDecoration: "underline" }}>
            {euro(result.retainedAmount)}
          </Typography>

          <Divider sx={{ gridColumn: "1 / -1", borderStyle: "dashed", mt: 1 }} />

          <Typography variant="caption" color="text.secondary" sx={{ gridColumn: "1 / -1" }}>
            {t(baseAmountLabel, { amount: euro(result.baseAmount) })} ·{" "}
            {t(`${TRANSLATION_BASE}.result.maxAssessment`, { amount: euro(result.maxAssessment) })}
          </Typography>
        </Box>
      )}
    </Stack>
  );
}
